import clsx from 'clsx';
import { formatDateSafe } from '@/utils/dateUtils';
import { ApprovalCardActions } from '@/features/Vacations/components/VacationApprovalCard/components/ApprovalCardActions';
import { ApprovalCardHeader } from '@/features/Vacations/components/VacationApprovalCard/components/ApprovalCardHeader';
import { formatDayOffDates } from '@/features/DpRequests/utils/formatDayOffDates';
import { TONE_CLASSES, describeRequestStatus } from '@/features/DpRequests/dpRequests.definitions';
import { DAY_OFF_CARD_LABELS, KIND_LABELS } from '../../approvals.definitions';
import type { ApprovalDecisionKind, DayOffApproval } from '../../approvals.types';
import { formatIsoDate, getDayOffDate, getDayOffDeadlineTone } from '../../utils/dayOffApprovalFormat';

interface DayOffApprovalCardProps {
    approval: DayOffApproval;
    onDecide: (approval: DayOffApproval, kind: ApprovalDecisionKind) => void;
    isActive?: boolean;
}

export function DayOffApprovalCard({ approval, onDecide, isActive = false }: DayOffApprovalCardProps) {
    const { request } = approval;
    const status = describeRequestStatus(request);
    const dayOffDate = getDayOffDate(request);
    const deadline = getDayOffDeadlineTone(dayOffDate);

    return (
        <article
            className={clsx(
                'flex h-full flex-col gap-4 rounded-2xl border bg-white p-5 shadow-sm transition-shadow',
                isActive ? 'border-primary-300 ring-1 ring-primary-200' : 'border-primary-100 hover:shadow-md'
            )}
            aria-label={DAY_OFF_CARD_LABELS.ariaLabel(approval.collaboratorName)}
        >
            <ApprovalCardHeader
                name={approval.collaboratorName}
                subtitle={
                    [approval.collaboratorRole, approval.collaboratorSquad].filter(Boolean).join(' · ') ||
                    approval.collaboratorEmail
                }
                collaboratorId={request.collaboratorId}
            />

            <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex w-fit items-center rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-700 font-montserrat">
                    {KIND_LABELS.day_off}
                </span>
                <span
                    className={clsx(
                        'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset font-montserrat',
                        TONE_CLASSES[status.tone]
                    )}
                >
                    {status.label}
                </span>
            </div>

            <div className="space-y-1">
                <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {DAY_OFF_CARD_LABELS.title}
                </p>
                <p className="text-sm text-raro-gray-700 font-montserrat">
                    {formatDayOffDates(request.days).join(' · ')}
                </p>
                {request.reason && (
                    <p className="text-xs text-raro-gray-500 font-montserrat">
                        {DAY_OFF_CARD_LABELS.reason}: {request.reason}
                    </p>
                )}
            </div>

            <dl className="mt-auto grid grid-cols-2 gap-3 border-t border-primary-100 pt-3">
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-raro-gray-400 font-montserrat">
                        {DAY_OFF_CARD_LABELS.requestedAt}
                    </dt>
                    <dd className="text-xs text-raro-gray-700 font-montserrat">
                        {formatDateSafe(approval.requestedAt)}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] uppercase tracking-wide text-raro-gray-400 font-montserrat">
                        {DAY_OFF_CARD_LABELS.expiresAt}
                    </dt>
                    <dd className={clsx('text-xs font-montserrat', deadline.className)}>
                        {formatIsoDate(dayOffDate)}
                        {deadline.hint ? ` · ${deadline.hint}` : ''}
                    </dd>
                </div>
            </dl>

            <ApprovalCardActions onDecide={(kind) => onDecide(approval, kind)} />
        </article>
    );
}
